if (!customElements.get('free-shipping-bar')) {
  const BAR_VISIBLE_DURATION = 5000;

  customElements.define('free-shipping-bar', class extends HTMLElement {
    constructor() {
      super();
      this.threshold = parseInt(this.dataset.threshold, 10);
      if (!this.threshold) return;
      this.unsubscribeCart = null;
      this.hideTimer = null;
    }

    handleUpdate = (evt) => {
      const subtotal = evt.cartData?.items_subtotal_price ?? evt.items_subtotal_price;
      if (typeof subtotal === 'number') this.render(subtotal);
    };

    connectedCallback() {
      if (!this.threshold) return;

      // Bar mode: full progress bar with labelled child message elements
      this.progressBar     = this.querySelector('[data-progress-bar]');
      this.initialMessage  = this.querySelector('[data-message-initial]');
      this.progressMessage = this.querySelector('[data-progress-message]');
      this.successMessage  = this.querySelector('[data-success-message]');

      // Text mode: element contains only a text node (nav bar usage)
      this.isTextMode = !this.progressMessage && !this.successMessage;

      this.moneyFormat             = this.dataset.moneyFormat;
      this.messageInitialTemplate  = this.dataset.messageInitial  || '';
      this.messageProgressTemplate = this.dataset.messageProgress || '';
      this.messageSuccessText      = this.dataset.messageSuccess  || '';
      this.displayDuration         = parseInt(this.dataset.displayDuration, 10) * 1000 || BAR_VISIBLE_DURATION;
      this.isCartContext            = this.dataset.context === 'cart';

      // Bar mode starts hidden — only shown on cart add (skipped for cart footer)
      if (!this.isTextMode && !this.isCartContext) {
        this.classList.add('fsb-bar');
      }

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleUpdate);
      }
    }

    disconnectedCallback() {
      clearTimeout(this.hideTimer);
      if (typeof this.unsubscribeCart === 'function') {
        this.unsubscribeCart();
        this.unsubscribeCart = null;
      }
    }

    render(subtotal) {
      const remaining = Math.max(0, this.threshold - subtotal);
      const progress  = Math.min(100, (subtotal / this.threshold) * 100);

      if (this.isTextMode) {
        this.renderText(subtotal, remaining);
      } else {
        this.renderBar(subtotal, remaining, progress);
      }
    }

    renderBar(subtotal, remaining, progress) {
      if (this.progressBar) this.progressBar.style.width = `${progress}%`;

      if (subtotal === 0) {
        // State 1: empty cart — show threshold message
        this.initialMessage?.removeAttribute('hidden');
        this.progressMessage.hidden = true;
        this.successMessage.hidden  = true;
      } else if (remaining > 0) {
        // State 2: cart has items, below threshold
        this.initialMessage?.setAttribute('hidden', '');
        this.progressMessage.textContent = this.messageProgressTemplate.replace('[amount]', this.formatMoney(remaining));
        this.progressMessage.hidden = false;
        this.successMessage.hidden  = true;
      } else {
        // State 3: threshold reached
        this.initialMessage?.setAttribute('hidden', '');
        this.progressMessage.hidden = true;
        this.successMessage.hidden  = false;
      }

      this.showBar();
    }

    showBar() {
      // Cart footer is always visible — skip show/hide toggling
      if (this.isCartContext) return;

      this.classList.add('fsb-bar--visible');

      // Reset auto-hide timer on every cart update
      clearTimeout(this.hideTimer);
      this.hideTimer = setTimeout(() => {
        this.classList.remove('fsb-bar--visible');
      }, this.displayDuration);
    }

    renderText(subtotal, remaining) {
      if (subtotal === 0) {
        this.textContent = this.messageInitialTemplate.replace('[amount]', this.formatMoney(this.threshold));
      } else if (remaining > 0) {
        this.textContent = this.messageProgressTemplate.replace('[amount]', this.formatMoney(remaining));
      } else {
        this.textContent = this.messageSuccessText;
      }
    }

    formatMoney(cents) {
      const format = this.moneyFormat || '{{amount}}';

      const formatAmount = (cents, decimalSep, thousandSep, precision) => {
        const [integer, decimal] = (cents / 100).toFixed(precision).split('.');
        const withThousands = integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSep);
        return precision > 0 ? withThousands + decimalSep + decimal : withThousands;
      };

      const FORMATS = {
        amount:                                  ['.', ',', 2],
        amount_no_decimals:                      ['.', ' ', 0],
        amount_with_comma_separator:             [',', '.', 2],
        amount_no_decimals_with_comma_separator: [',', '.', 0],
        amount_with_apostrophe_separator:        ['.', "'", 2],
        amount_with_space_separator:             [',', ' ', 2],
        amount_no_decimals_with_space_separator: ['.', ' ', 0],
      };

      return Object.entries(FORMATS).reduce(
        (result, [key, [decimalSep, thousandSep, precision]]) =>
          result.replace(`{{${key}}}`, formatAmount(cents, decimalSep, thousandSep, precision)),
        format
      );
    }
  });
}
